<template>
  <div class="login-container">
    <div class="card">
      <h2>{{ isRegister ? 'Create Account' : 'Login' }}</h2>
      <form @submit.prevent="handleSubmit">
        <div class="form-group">
          <label>Username</label>
          <input 
            v-model="username" 
            type="text" 
            required 
            :disabled="auth.loading.value"
            placeholder="Enter username"
          />
        </div>
        <div class="form-group">
          <label>Password</label>
          <input 
            v-model="password" 
            type="password" 
            required 
            :disabled="auth.loading.value"
            placeholder="Enter password"
          />
        </div>
        <button type="submit" class="submit-btn" :disabled="auth.loading.value">
          <span v-if="!auth.loading.value">
            {{ isRegister ? 'Register' : 'Login' }}
          </span>
          <span v-else>
            <LoadingSkeleton variant="spinner" :count="1" message="Please wait..." />
          </span>
        </button>
      </form>
      <p class="toggle-text">
        {{ isRegister ? 'Already have an account?' : "Don't have an account?" }}
        <a href="#" @click.prevent="toggleMode">
          {{ isRegister ? 'Login here' : 'Register here' }}
        </a>
      </p>
      <p v-if="auth.error.value" class="error">{{ auth.error.value }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import LoadingSkeleton from '@/components/LoadingSkeleton.vue';

const router = useRouter();
const auth = useAuth();

const username = ref('');
const password = ref('');
const isRegister = ref(false);

/**
 * Handle form submission for login or registration
 */
async function handleSubmit() {
  let success = false;

  if (isRegister.value) {
    success = await auth.register(username.value, password.value);
  } else {
    success = await auth.login(username.value, password.value);
  }

  if (success) {
    // Redirect to home page on success
    router.push('/');
  }
}

/**
 * Toggle between login and register modes
 */
function toggleMode() {
  isRegister.value = !isRegister.value;
  auth.error.value = ''; // Clear any errors when toggling
}
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}
.card {
  background: white;
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 400px;
}
h2 {
  text-align: center;
  margin-top: 0;
  color: #2a3a4a;
}
.form-group {
  margin-bottom: 1rem;
}
label {
  display: block;
  margin-bottom: 0.5rem;
  color: #666;
  font-weight: 500;
}
input {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-sizing: border-box;
  font-size: 1rem;
  transition: border-color 0.3s;
}
input:focus {
  outline: none;
  border-color: #4f8cff;
}
input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}
.submit-btn {
  width: 100%;
  padding: 0.8rem;
  background: #4f8cff;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s;
  min-height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.submit-btn:hover:not(:disabled) {
  background: #3b7ddd;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 140, 255, 0.4);
}
.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}
.toggle-text {
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #666;
}
.toggle-text a {
  color: #4f8cff;
  text-decoration: none;
  font-weight: 600;
}
.toggle-text a:hover {
  text-decoration: underline;
}
.error {
  color: #f56565;
  text-align: center;
  margin-top: 1rem;
  padding: 0.5rem;
  background: #fed7d7;
  border-radius: 6px;
  font-size: 0.9rem;
}
</style>
